import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { SERVICES } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | already | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        source: "footer",
      });
      if (data.status === "already_subscribed") {
        setState("already");
      } else {
        setState("success");
      }
    } catch (err) {
      setState("error");
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError("That doesn't look like a valid email address.");
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    }
  };

  if (state === "success" || state === "already") {
    return (
      <div
        className="mt-16 pt-8 border-t border-zinc-900"
        data-testid="footer-newsletter-success"
      >
        <div className="max-w-2xl">
          <p className="label-eyebrow text-accent-blue">Newsletter</p>
          <p className="font-heading font-bold text-2xl md:text-3xl mt-3 leading-tight">
            {state === "success"
              ? "You're on the list."
              : "You're already subscribed."}
          </p>
          <p className="text-sm text-zinc-400 mt-2">
            {state === "success"
              ? "Check your inbox for a welcome email. We publish three times a week."
              : "Thanks for the enthusiasm - you'll be with us on the next send."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16 pt-8 border-t border-zinc-900">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="label-eyebrow text-accent-blue">Newsletter</p>
          <p className="font-heading font-bold text-2xl md:text-3xl mt-3 leading-tight">
            Join 16,000+ highways professionals.
          </p>
          <p className="text-sm text-zinc-400 mt-2 max-w-md">
            The best of UK highways and infrastructure, delivered three times a
            week. No fluff, no filler.
          </p>
        </div>
        <form
          onSubmit={submit}
          data-testid="footer-newsletter-form"
          className="lg:col-span-7 flex flex-col sm:flex-row gap-3 lg:pt-2"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@work-email.com"
            data-testid="footer-newsletter-email"
            className="flex-1 min-w-0 rounded-md border border-zinc-300 focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 px-4 py-3 text-sm outline-none transition"
          />
          <button
            type="submit"
            disabled={state === "loading"}
            data-testid="footer-newsletter-submit"
            className="inline-flex items-center justify-center gap-2 bg-accent-blue hover:brightness-110 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-md text-sm transition whitespace-nowrap"
          >
            {state === "loading" ? "Subscribing…" : "Subscribe"}
          </button>
        </form>
      </div>
      {state === "error" && (
        <p
          className="mt-3 text-sm text-red-500"
          data-testid="footer-newsletter-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-zinc-950 border-t border-zinc-800 mt-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center">
              <img
                src="/highways-industry-logo.png"
                alt="HighwaysIndustry.com"
                className="h-10 w-auto"
              />
            </div>
            <p className="label-eyebrow text-accent-blue mt-3">
              Create · Amplify · Position
            </p>
            <p className="font-heading font-bold text-3xl md:text-4xl mt-8 leading-tight max-w-md">
              Great companies deserve to be seen.
            </p>
            <p className="text-zinc-400 mt-6 max-w-md">
              We create content, grow audiences and build brands across the
              highways and infrastructure sector.
            </p>
            <Link
              to="/positioning-review"
              data-testid="footer-cta"
              className="mt-8 inline-flex items-center gap-2 bg-primary-blue text-white px-5 py-3 text-sm font-semibold hover:glow-blue transition-all"
            >
              Book A Discovery Call →
            </Link>
          </div>

          <div className="lg:col-span-3">
            <p className="label-eyebrow text-zinc-500">Services</p>
            <ul className="mt-5 space-y-2">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="label-eyebrow text-zinc-500">Site</p>
            <ul className="mt-5 space-y-2">
              <li>
                <Link to="/what-we-do" className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue">What We Do</Link>
              </li>
              <li>
                <Link to="/industry-reach" className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue">Industry Reach</Link>
              </li>
              <li>
                <Link to="/case-studies" className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue">Case Studies</Link>
              </li>
              <li>
                <Link to="/about" className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue">About</Link>
              </li>
              <li>
                <Link to="/contact" className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue">Contact</Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="label-eyebrow text-zinc-500">Our Group</p>
            <ul className="mt-5 space-y-2">
              <li>
                <a
                  href="https://highwaysindustry.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-media-platform"
                  className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue"
                >
                  HighwaysIndustry.com ↗
                </a>
                <p className="text-xs text-zinc-500 -mt-1 mb-2">Industry news &amp; features</p>
              </li>
              <li>
                <a
                  href="https://highwayslens.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-portfolio"
                  className="inline-block py-2 text-sm text-zinc-300 hover:text-accent-blue"
                >
                  Highways Lens ↗
                </a>
                <p className="text-xs text-zinc-500 -mt-1">Photography &amp; film portfolio</p>
              </li>
            </ul>
          </div>
        </div>

        <NewsletterSignup />

        <div className="mt-16 pt-8 border-t border-zinc-900 flex flex-col md:flex-row justify-between gap-4 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} HighwaysIndustry.com. All rights reserved.</p>
          <p>Create. Amplify. Position.</p>
        </div>
      </div>
    </footer>
  );
}
